import {
  Template,
  TemplateBlock,
  TemplatePart,
  MergeField,
  StyleProperties,
  SpacingValues,
} from '../types/template';

// ID Generation
export const generateId = (): string => {
  return `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
};

// Template Validation
export interface ValidationError {
  field: string;
  message: string;
  severity: 'error' | 'warning';
}

export interface ValidationResult {
  isValid: boolean;
  errors: ValidationError[];
  warnings: ValidationError[];
}

// Utility functions
export const isValidCSSValue = (value: string): boolean => {
  const cssValueRegex = /^-?\d*\.?\d+(px|em|rem|%|vh|vw|pt|pc|in|cm|mm|ex|ch|vmin|vmax)?$/;
  return cssValueRegex.test(value) || value === 'auto' || value === 'inherit' || value === 'initial';
};

export const isValidColor = (color: string): boolean => {
  const hexRegex = /^#([A-Fa-f0-9]{6}|[A-Fa-f0-9]{3})$/;
  const rgbRegex = /^rgb\(\s*\d+\s*,\s*\d+\s*,\s*\d+\s*\)$/;
  const rgbaRegex = /^rgba\(\s*\d+\s*,\s*\d+\s*,\s*\d+\s*,\s*(0|1|0?\.\d+)\s*\)$/;
  const namedColors = ['transparent', 'black', 'white', 'red', 'green', 'blue', 'yellow', 'orange', 'purple', 'pink', 'gray', 'grey'];
  
  return hexRegex.test(color) || 
         rgbRegex.test(color) || 
         rgbaRegex.test(color) || 
         namedColors.includes(color.toLowerCase());
};

export const isValidEmail = (email: string): boolean => {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return emailRegex.test(email);
};

export const isValidUrl = (url: string): boolean => {
  try {
    new URL(url);
    return true;
  } catch {
    return false;
  }
};

// System merge fields that are always available
export const getSystemMergeFields = (): MergeField[] => [
  {
    id: 'system-first-name',
    name: 'First Name',
    key: 'first_name',
    type: 'text',
    required: false,
    category: 'system',
    isSystem: true,
    description: 'Recipient\'s first name',
  },
  {
    id: 'system-last-name',
    name: 'Last Name',
    key: 'last_name',
    type: 'text',
    required: false,
    category: 'system',
    isSystem: true,
    description: 'Recipient\'s last name',
  },
  {
    id: 'system-email',
    name: 'Email Address',
    key: 'email',
    type: 'email',
    required: true,
    category: 'system',
    isSystem: true,
    description: 'Recipient\'s email address',
  },
  {
    id: 'system-current-date',
    name: 'Current Date',
    key: 'current_date',
    type: 'date',
    required: false,
    category: 'system',
    isSystem: true,
    description: 'Current date when email is sent',
  },
  {
    id: 'system-current-year',
    name: 'Current Year',
    key: 'current_year',
    type: 'number',
    required: false,
    category: 'system',
    isSystem: true,
    description: 'Current year when email is sent',
  },
  {
    id: 'system-unsubscribe-url',
    name: 'Unsubscribe URL',
    key: 'unsubscribe_url',
    type: 'url',
    required: true,
    category: 'system',
    isSystem: true,
    description: 'URL for recipient to unsubscribe',
  },
];

// Template Parts Utilities
export const createTemplatePartFromBlocks = (
  blocks: TemplateBlock[],
  name: string,
  type: TemplatePart['type'],
  category: string = 'custom'
): TemplatePart => {
  return {
    id: generateId(),
    name,
    type,
    content: blocks.map(block => ({
      ...block,
      id: generateId(), // Generate new IDs for the copied blocks
    })),
    category,
    isGlobal: false,
    brandGuidelines: {
      enforceColors: false,
      enforceFonts: false,
      enforceSpacing: false,
    },
    metadata: {
      createdAt: new Date(),
      updatedAt: new Date(),
      createdBy: 'current-user', // This would come from auth context
      usageCount: 0,
      tags: [],
      version: 1,
    },
  };
};

export const applyTemplatePartToTemplate = (
  template: Template,
  templatePart: TemplatePart,
  insertIndex?: number
): Template => {
  const newBlocks = templatePart.content.map(block => ({
    ...block,
    id: generateId(), // Generate new IDs to avoid conflicts
    templatePartId: templatePart.id,
  }));

  const updatedContent = [...template.content];
  
  if (typeof insertIndex === 'number') {
    updatedContent.splice(insertIndex, 0, ...newBlocks);
  } else {
    updatedContent.push(...newBlocks);
  }

  // Reorder blocks
  updatedContent.forEach((block, index) => {
    block.position.order = index;
  });

  return {
    ...template,
    content: updatedContent,
    metadata: {
      ...template.metadata,
      updatedAt: new Date(),
    },
    personalization: {
      ...template.personalization,
      templateParts: [
        ...(template.personalization?.templateParts || []),
        templatePart.id,
      ],
    },
  };
};

export const validateTemplatePart = (templatePart: TemplatePart): ValidationResult => {
  const errors: ValidationError[] = [];
  const warnings: ValidationError[] = [];

  // Validate basic properties
  if (!templatePart.name?.trim()) {
    errors.push({
      field: 'name',
      message: 'Template part name is required',
      severity: 'error',
    });
  }

  if (!templatePart.type) {
    errors.push({
      field: 'type',
      message: 'Template part type is required',
      severity: 'error',
    });
  }

  if (!templatePart.content || templatePart.content.length === 0) {
    warnings.push({
      field: 'content',
      message: 'Template part has no content blocks',
      severity: 'warning',
    });
  }

  // Validate content blocks
  templatePart.content.forEach((block, index) => {
    const blockValidation = validateTemplateBlock(block);
    blockValidation.errors.forEach(error => {
      errors.push({
        ...error,
        field: `content[${index}].${error.field}`,
      });
    });
    blockValidation.warnings.forEach(warning => {
      warnings.push({
        ...warning,
        field: `content[${index}].${warning.field}`,
      });
    });
  });

  return {
    isValid: errors.length === 0,
    errors,
    warnings,
  };
};

export const validateTemplateBlock = (block: TemplateBlock): ValidationResult => {
  const errors: ValidationError[] = [];
  const warnings: ValidationError[] = [];

  // Validate basic properties
  if (!block.id) {
    errors.push({
      field: 'id',
      message: 'Block ID is required',
      severity: 'error',
    });
  }

  if (!block.type) {
    errors.push({
      field: 'type',
      message: 'Block type is required',
      severity: 'error',
    });
  }

  // Validate styles
  if (block.styles.typography?.color && !isValidColor(block.styles.typography.color)) {
    errors.push({
      field: 'styles.typography.color',
      message: 'Invalid color format',
      severity: 'error',
    });
  }

  if (block.styles.layout?.width && !isValidCSSValue(block.styles.layout.width)) {
    errors.push({
      field: 'styles.layout.width',
      message: 'Invalid CSS width value',
      severity: 'error',
    });
  }

  if (block.styles.layout?.height && !isValidCSSValue(block.styles.layout.height)) {
    errors.push({
      field: 'styles.layout.height',
      message: 'Invalid CSS height value',
      severity: 'error',
    });
  }

  // Validate spacing values
  if (block.styles.layout?.margin) {
    Object.entries(block.styles.layout.margin).forEach(([side, value]) => {
      if (value && !isValidCSSValue(value)) {
        errors.push({
          field: `styles.layout.margin.${side}`,
          message: `Invalid margin ${side} value`,
          severity: 'error',
        });
      }
    });
  }

  if (block.styles.layout?.padding) {
    Object.entries(block.styles.layout.padding).forEach(([side, value]) => {
      if (value && !isValidCSSValue(value)) {
        errors.push({
          field: `styles.layout.padding.${side}`,
          message: `Invalid padding ${side} value`,
          severity: 'error',
        });
      }
    });
  }

  return {
    isValid: errors.length === 0,
    errors,
    warnings,
  };
};

// Brand Guidelines Enforcement
export const enforceBrandGuidelines = (
  styles: StyleProperties,
  brandGuidelines: TemplatePart['brandGuidelines'],
  brandColors?: Record<string, string>,
  brandFonts?: Record<string, string>,
  brandSpacing?: Record<string, string>
): StyleProperties => {
  if (!brandGuidelines) return styles;

  const enforcedStyles = { ...styles };

  // Enforce colors
  if (brandGuidelines.enforceColors && brandColors && styles.typography?.color) {
    const colorValues = Object.values(brandColors);
    if (!colorValues.includes(styles.typography.color)) {
      // Find closest brand color or use default
      enforcedStyles.typography = {
        ...enforcedStyles.typography,
        color: brandColors.primary || brandColors.text || colorValues[0],
      };
    }
  }

  // Enforce fonts
  if (brandGuidelines.enforceFonts && brandFonts && styles.typography?.fontFamily) {
    const fontValues = Object.values(brandFonts);
    const currentFont = styles.typography.fontFamily;
    
    if (!fontValues.some(font => currentFont.includes(font))) {
      enforcedStyles.typography = {
        ...enforcedStyles.typography,
        fontFamily: brandFonts.primary || fontValues[0],
      };
    }
  }

  // Enforce spacing
  if (brandGuidelines.enforceSpacing && brandSpacing) {
    const spacingValues = Object.values(brandSpacing);
    
    // Enforce margin spacing
    if (styles.layout?.margin) {
      const enforcedMargin = { ...styles.layout.margin };
      Object.keys(enforcedMargin).forEach(side => {
        const currentValue = enforcedMargin[side as keyof SpacingValues];
        if (currentValue && !spacingValues.includes(currentValue)) {
          // Find closest spacing value
          const numericValue = parseInt(currentValue);
          const closestSpacing = spacingValues.reduce((closest, spacing) => {
            const spacingNum = parseInt(spacing);
            const currentClosest = parseInt(closest);
            return Math.abs(spacingNum - numericValue) < Math.abs(currentClosest - numericValue)
              ? spacing
              : closest;
          });
          enforcedMargin[side as keyof SpacingValues] = closestSpacing;
        }
      });
      
      if (enforcedStyles.layout) {
        enforcedStyles.layout.margin = enforcedMargin;
      }
    }

    // Enforce padding spacing
    if (styles.layout?.padding) {
      const enforcedPadding = { ...styles.layout.padding };
      Object.keys(enforcedPadding).forEach(side => {
        const currentValue = enforcedPadding[side as keyof SpacingValues];
        if (currentValue && !spacingValues.includes(currentValue)) {
          // Find closest spacing value
          const numericValue = parseInt(currentValue);
          const closestSpacing = spacingValues.reduce((closest, spacing) => {
            const spacingNum = parseInt(spacing);
            const currentClosest = parseInt(closest);
            return Math.abs(spacingNum - numericValue) < Math.abs(currentClosest - numericValue)
              ? spacing
              : closest;
          });
          enforcedPadding[side as keyof SpacingValues] = closestSpacing;
        }
      });
      
      if (enforcedStyles.layout) {
        enforcedStyles.layout.padding = enforcedPadding;
      }
    }
  }

  return enforcedStyles;
};

// Template Part Search and Filtering
export const searchTemplateParts = (
  templateParts: TemplatePart[],
  searchTerm: string,
  filters: {
    type?: string;
    category?: string;
    isGlobal?: boolean;
    tags?: string[];
  } = {}
): TemplatePart[] => {
  return templateParts.filter(part => {
    // Text search
    const matchesSearch = !searchTerm || 
      part.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      part.description?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      part.metadata.tags.some(tag => tag.toLowerCase().includes(searchTerm.toLowerCase()));

    // Type filter
    const matchesType = !filters.type || filters.type === 'all' || part.type === filters.type;

    // Category filter
    const matchesCategory = !filters.category || filters.category === 'all' || part.category === filters.category;

    // Global filter
    const matchesGlobal = filters.isGlobal === undefined || part.isGlobal === filters.isGlobal;

    // Tags filter
    const matchesTags = !filters.tags || filters.tags.length === 0 || 
      filters.tags.some(tag => part.metadata.tags.includes(tag));

    return matchesSearch && matchesType && matchesCategory && matchesGlobal && matchesTags;
  });
};

// Generate thumbnail for template part
export const generateTemplatePartThumbnail = (templatePart: TemplatePart): string => {
  // In a real implementation, this would generate an actual thumbnail image
  // For now, return a placeholder based on the part type
  const thumbnailMap: Record<TemplatePart['type'], string> = {
    header: 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMzIwIiBoZWlnaHQ9IjE4MCIgdmlld0JveD0iMCAwIDMyMCAxODAiIGZpbGw9Im5vbmUiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+CjxyZWN0IHdpZHRoPSIzMjAiIGhlaWdodD0iMTgwIiBmaWxsPSIjRjNGNEY2Ii8+CjxyZWN0IHg9IjIwIiB5PSIyMCIgd2lkdGg9IjI4MCIgaGVpZ2h0PSI2MCIgZmlsbD0iIzNCODJGNiIvPgo8L3N2Zz4K',
    footer: 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMzIwIiBoZWlnaHQ9IjE4MCIgdmlld0JveD0iMCAwIDMyMCAxODAiIGZpbGw9Im5vbmUiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+CjxyZWN0IHdpZHRoPSIzMjAiIGhlaWdodD0iMTgwIiBmaWxsPSIjRjNGNEY2Ii8+CjxyZWN0IHg9IjIwIiB5PSIxMDAiIHdpZHRoPSIyODAiIGhlaWdodD0iNjAiIGZpbGw9IiM2QjcyODAiLz4KPC9zdmc+Cg==',
    section: 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMzIwIiBoZWlnaHQ9IjE4MCIgdmlld0JveD0iMCAwIDMyMCAxODAiIGZpbGw9Im5vbmUiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+CjxyZWN0IHdpZHRoPSIzMjAiIGhlaWdodD0iMTgwIiBmaWxsPSIjRjNGNEY2Ii8+CjxyZWN0IHg9IjIwIiB5PSI0MCIgd2lkdGg9IjI4MCIgaGVpZ2h0PSIxMDAiIGZpbGw9IiMxMEI5ODEiLz4KPC9zdmc+Cg==',
    sidebar: 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMzIwIiBoZWlnaHQ9IjE4MCIgdmlld0JveD0iMCAwIDMyMCAxODAiIGZpbGw9Im5vbmUiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+CjxyZWN0IHdpZHRoPSIzMjAiIGhlaWdodD0iMTgwIiBmaWxsPSIjRjNGNEY2Ii8+CjxyZWN0IHg9IjIwIiB5PSIyMCIgd2lkdGg9IjgwIiBoZWlnaHQ9IjE0MCIgZmlsbD0iI0Y1OUUwQiIvPgo8L3N2Zz4K',
    hero: 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMzIwIiBoZWlnaHQ9IjE4MCIgdmlld0JveD0iMCAwIDMyMCAxODAiIGZpbGw9Im5vbmUiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+CjxyZWN0IHdpZHRoPSIzMjAiIGhlaWdodD0iMTgwIiBmaWxsPSIjRjNGNEY2Ii8+CjxyZWN0IHg9IjIwIiB5PSIyMCIgd2lkdGg9IjI4MCIgaGVpZ2h0PSIxNDAiIGZpbGw9IiM4QjVDRjYiLz4KPC9zdmc+Cg==',
    cta: 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMzIwIiBoZWlnaHQ9IjE4MCIgdmlld0JveD0iMCAwIDMyMCAxODAiIGZpbGw9Im5vbmUiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+CjxyZWN0IHdpZHRoPSIzMjAiIGhlaWdodD0iMTgwIiBmaWxsPSIjRjNGNEY2Ii8+CjxyZWN0IHg9IjEwMCIgeT0iNzAiIHdpZHRoPSIxMjAiIGhlaWdodD0iNDAiIGZpbGw9IiNFRjQ0NDQiLz4KPC9zdmc+Cg=='
  };

  return thumbnailMap[templatePart.type] || thumbnailMap.section;
};